import { SUPPORTED_LANGUAGES } from "@/utils/library/languages";

/**
 * Query-string key used to carry demo-funnel attribution through to the
 * contact-sales / book-a-demo page. Every internal CTA that links to that page
 * should append this so the form submission knows where the visitor came from.
 */
export const DEMO_SOURCE_PARAM = "source";

/** Value used when a visitor lands on the demo page without any attribution. */
export const DEMO_SOURCE_DIRECT = "direct";

const LOCALES = new Set<string>(SUPPORTED_LANGUAGES as readonly string[]);

/**
 * Derives a coarse page key from a (possibly localized) pathname for use in
 * attribution source strings. Drops a leading locale segment.
 *
 * Examples:
 *  - "/en/pricing" -> "pricing"
 *  - "/es"         -> "home"
 *  - "/product"    -> "product"
 *  - "/"           -> "home"
 */
export function getDemoPageKey(pathname: string | null | undefined): string {
  if (!pathname) return "unknown";
  const segments = pathname.split("/").filter(Boolean);
  if (segments.length > 0 && LOCALES.has(segments[0])) {
    segments.shift();
  }
  return segments[0] ?? "home";
}

/**
 * Appends the attribution source to an internal href, preserving any existing
 * query parameters. Returns the href unchanged when no source is provided.
 */
export function withDemoSource(href: string, source: string): string {
  if (!source) return href;
  const [path, hash] = href.split("#");
  const [basePath, existingQuery = ""] = path.split("?");
  const params = new URLSearchParams(existingQuery);
  params.set(DEMO_SOURCE_PARAM, source);
  const query = params.toString();
  return `${basePath}?${query}${hash ? `#${hash}` : ""}`;
}

